import { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import SignatureCanvas from 'react-signature-canvas';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ant design icons
import UploadOutlined from '@ant-design/icons/UploadOutlined';

// ==============================|| SIGNATURE PAD ||============================== //

const SignaturePad = forwardRef(({ onEnd, label, clearLabel, signHereLabel, uploadLabel, initialDataUrl }, ref) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const seededRef = useRef(false);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 150 });

  const updateCanvasSize = useCallback(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      setCanvasSize({ width, height: 150 });
    }
  }, []);

  useEffect(() => {
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [updateCanvasSize]);

  // Seed the canvas once with the saved signature (if any). Runs after the
  // container has measured its real width — otherwise fromDataURL renders the
  // image at the placeholder 300px width and it can appear clipped on wider layouts.
  useEffect(() => {
    if (seededRef.current) return;
    if (!initialDataUrl || !canvasRef.current) return;
    if (!containerRef.current || canvasSize.width <= 0) return;
    try {
      canvasRef.current.fromDataURL(initialDataUrl, { ratio: 1, width: canvasSize.width, height: 150 });
      seededRef.current = true;
    } catch (err) {
      console.warn('[SignaturePad] failed to seed from initialDataUrl:', err?.message);
    }
  }, [initialDataUrl, canvasSize.width]);

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      isEmpty: () => (canvasRef.current ? canvasRef.current.isEmpty() : true),
      toDataURL: (type) => (canvasRef.current ? canvasRef.current.toDataURL(type) : ''),
      fromDataURL: (dataUrl) => {
        if (canvasRef.current && dataUrl) {
          canvasRef.current.fromDataURL(dataUrl, { ratio: 1, width: canvasSize.width, height: 150 });
        }
      },
      clear: () => canvasRef.current && canvasRef.current.clear()
    }),
    [canvasSize.width]
  );

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
    if (onEnd) onEnd('');
  };

  const handleEnd = () => {
    if (onEnd && canvasRef.current && !canvasRef.current.isEmpty()) {
      onEnd(canvasRef.current.toDataURL('image/png'));
    }
  };

  const handleUpload = () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (canvasRef.current) {
        canvasRef.current.clear();
        canvasRef.current.fromDataURL(reader.result, { ratio: 1 });
        if (onEnd) {
          setTimeout(() => {
            if (canvasRef.current && !canvasRef.current.isEmpty()) {
              onEnd(canvasRef.current.toDataURL('image/png'));
            }
          }, 300);
        }
      }
    };
    reader.readAsDataURL(file);
    fileInputRef.current.value = '';
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      <Box
        ref={containerRef}
        sx={{
          border: `2px solid ${theme.palette.grey[300]}`,
          borderRadius: 1,
          bgcolor: theme.palette.grey[50],
          position: 'relative',
          '&:hover': {
            borderColor: theme.palette.grey[400]
          }
        }}
      >
        <SignatureCanvas
          ref={canvasRef}
          backgroundColor="rgb(250, 250, 250)"
          penColor={theme.palette.grey[800]}
          canvasProps={{
            width: canvasSize.width,
            height: canvasSize.height,
            style: {
              touchAction: 'none',
              borderRadius: '4px'
            }
          }}
          onEnd={handleEnd}
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            color: theme.palette.grey[400],
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        >
          {signHereLabel || 'Sign here'}
        </Typography>
      </Box>
      {(clearLabel || uploadLabel) && (
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
          {clearLabel ? (
            <Button size="small" onClick={handleClear}>
              {clearLabel}
            </Button>
          ) : (
            <span />
          )}
          {uploadLabel && (
            <>
              <Button size="small" startIcon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
                {uploadLabel}
              </Button>
              <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handleUpload} />
            </>
          )}
        </Stack>
      )}
    </Box>
  );
});

SignaturePad.displayName = 'SignaturePad';

SignaturePad.propTypes = {
  onEnd: PropTypes.func,
  label: PropTypes.string,
  clearLabel: PropTypes.string,
  signHereLabel: PropTypes.string,
  uploadLabel: PropTypes.string,
  initialDataUrl: PropTypes.string
};

export default SignaturePad;
